import {
  type LeadListScope,
  type LeadStatus,
  LeadListScope as LeadListScopeEnum,
} from '@up2stay/up2promo-api-client';

import { isLeadStatus } from '@/core/components/leads/lead-status-colors';

/** Read `?status=` (comma-separated) from the Leads list search. */
export function parseLeadsStatusFromSearch(search: string): LeadStatus[] {
  const raw = new URLSearchParams(search).get('status')?.trim();
  if (!raw) return [];
  const next: LeadStatus[] = [];
  const seen = new Set<string>();
  for (const part of raw.split(',')) {
    const value = part.trim();
    if (!isLeadStatus(value) || seen.has(value)) continue;
    seen.add(value);
    next.push(value);
  }
  return next;
}

export function leadsStatusSearchKey(statuses: readonly LeadStatus[]): string {
  return [...statuses].join(',');
}

export function parseLeadsScopeFromSearch(
  search: string,
): LeadListScope | null {
  const raw = new URLSearchParams(search).get('scope')?.trim();
  if (
    raw === LeadListScopeEnum.MINE ||
    raw === LeadListScopeEnum.WORKSPACE ||
    raw === LeadListScopeEnum.OTHERS_PRIVATE
  ) {
    return raw;
  }
  return null;
}

export type LeadsView = 'list' | 'boards';

/** Read `?view=board|list` (DEV-567); `null` when the URL does not say. */
export function parseLeadsViewFromSearch(search: string): LeadsView | null {
  const raw = new URLSearchParams(search).get('view')?.trim();
  if (raw === 'board') return 'boards';
  if (raw === 'list') return 'list';
  return null;
}

export function parseLeadsQueryFromSearch(search: string): string {
  return new URLSearchParams(search).get('q')?.trim() ?? '';
}

/** Merge scope / q / status into the current search string (preserves other params). */
export function buildLeadsListSearch(opts: {
  currentSearch: string;
  scope?: LeadListScope | null;
  q?: string | null;
  status?: readonly LeadStatus[] | null;
  /** List is the default, so only the board is written to the URL. */
  view?: LeadsView;
}): string {
  const params = new URLSearchParams(opts.currentSearch);
  if (opts.view === 'boards') params.set('view', 'board');
  else if (opts.view === 'list') params.delete('view');
  if (opts.scope != null) {
    params.set('scope', opts.scope);
  }
  if (opts.q !== undefined) {
    const trimmed = opts.q?.trim() ?? '';
    if (trimmed) params.set('q', trimmed);
    else params.delete('q');
  }
  if (opts.status !== undefined) {
    const key = opts.status?.length ? leadsStatusSearchKey(opts.status) : '';
    if (key) params.set('status', key);
    else params.delete('status');
  }
  const s = params.toString();
  return s ? `?${s}` : '';
}
